/* DeviceActionsRenderer.js */

"use strict";

class DeviceActionsColumnRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("device-actions-column");
        this._rootElem.component = this._model;

        this._defaultSnapshotIcon = document.createElement("actionable-icon-gl");
        this._defaultSnapshotIcon.dataset.icon = "common.star";
        this._defaultSnapshotIcon.setAttribute("title", "Set Default Configuration");
        this._editIcon = document.createElement("actionable-icon-gl");
        this._editIcon.dataset.icon = "common.edit";
        this._editIcon.setAttribute("title", "Edit Snapshot Name");
        this._multiEditIcon = document.createElement("multiedit-icon-gl");
        this._multiEditIcon.dataset.icon = "common.multiedit";
        this._multiEditIcon.setAttribute("title", "Multi Edit");
        this._rollbackIcon = document.createElement("actionable-icon-gl");
        this._rollbackIcon.dataset.icon = "configdrift.roll_back";
        this._rollbackIcon.setAttribute("title", "Roll Back Snapshot");
        this._viewIcon = document.createElement("actionable-icon-gl");
        this._viewIcon.dataset.icon = "configdrift.eye";
        this._viewIcon.setAttribute("title", "View Snapshot");
        this._compareIcon = document.createElement("actionable-icon-gl");
        this._compareIcon.dataset.icon = "configdrift.compare";
        this._compareIcon.setAttribute("title", "Compare with Default");

        this._restoreIcon = document.createElement("actionable-icon-gl");
        this._restoreIcon.dataset.icon = "devicemanager.restore";
        this._restoreIcon.setAttribute("title", "Restore from backup");

        this._deleteIcon = document.createElement("actionable-icon-gl");
        this._deleteIcon.dataset.icon = "common.trash_bin";
        this._deleteIcon.setAttribute("title", "Delete Snapshot");

        this._rootElem.appendChild(this._defaultSnapshotIcon);
        this._rootElem.appendChild(this._multiEditIcon);
        this._rootElem.appendChild(this._editIcon);
        this._rootElem.appendChild(this._rollbackIcon);
        this._rootElem.appendChild(this._viewIcon);
        this._rootElem.appendChild(this._compareIcon);

        if (this._model._isRestoreRunAvailable && this._model._isOsSupported) {
            this._rootElem.appendChild(this._restoreIcon);
        }

        this._rootElem.appendChild(this._deleteIcon);

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _shutdown() {
        this._unregisterListeners();
        let prom = new Promise((resolve /*,reject*/) => {
            this._defaultSnapshotIcon.remove();
            this._defaultSnapshotIcon = undefined;
            this._editIcon.remove();
            this._editIcon = undefined;
            this._rollbackIcon.remove();
            this._rollbackIcon = undefined;
            this._viewIcon.remove();
            this._viewIcon = undefined;
            this._compareIcon.remove();
            this._compareIcon = undefined;
            this._deleteIcon.remove();
            this._deleteIcon = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._model = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on(
            "DefaultSnapshot",
            (this._onDefaultSnapshot = this._onDefaultSnapshot.bind(this))
        );
        this._model.on("Edit", (this._onEdit = this._onEdit.bind(this)));
        this._model.on("Rollback", (this._onRollback = this._onRollback.bind(this)));
        this._model.on("View", (this._onView = this._onView.bind(this)));
        this._model.on("Compare", (this._onCompare = this._onCompare.bind(this)));
        this._model.on("Delete", (this._onDelete = this._onDelete.bind(this)));
        this._defaultSnapshotIcon.addEventListener(
            "click",
            (this._onDefaultSnapshotIconUserClick = this._onDefaultSnapshotIconUserClick.bind(this))
        );
        this._editIcon.addEventListener(
            "click",
            (this._onEditIconUserClick = this._onEditIconUserClick.bind(this))
        );
        this._rollbackIcon.addEventListener(
            "click",
            (this._onRollbackIconUserClick = this._onRollbackIconUserClick.bind(this))
        );
        this._viewIcon.addEventListener(
            "click",
            (this._onViewIconUserClick = this._onViewIconUserClick.bind(this))
        );
        this._compareIcon.addEventListener(
            "click",
            (this._onCompareIconUserClick = this._onCompareIconUserClick.bind(this))
        );
        if (this._model._isRestoreRunAvailable) {
            this._restoreIcon.addEventListener(
                "click",
                (this._onRestoreIconUserClick = this._onRestoreIconUserClick.bind(this))
            );
        }
        this._deleteIcon.addEventListener(
            "click",
            (this._onDeleteIconUserClick = this._onDeleteIconUserClick.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeAllListeners("DefaultSnapshot");
        this._model.removeAllListeners("Edit");
        this._model.removeAllListeners("Rollback");
        this._model.removeAllListeners("View");
        this._model.removeAllListeners("Compare");
        this._model.removeAllListeners("Delete");
        this._defaultSnapshotIcon.removeEventListener(
            "click",
            this._onDefaultSnapshotIconUserClick
        );
        this._editIcon.removeEventListener("click", this._onEditIconUserClick);
        this._rollbackIcon.removeEventListener("click", this._onRollbackIconUserClick);
        this._viewIcon.removeEventListener("click", this._onViewIconUserClick);
        this._compareIcon.removeEventListener("click", this._onCompareIconUserClick);
        if (this._model._isRestoreRunAvailable) {
            this._restoreIcon.removeEventListener("click", this._onRestoreIconUserClick);
        }
        this._deleteIcon.removeEventListener("click", this._onDeleteIconUserClick);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._setDefaultSnapshot(this._model.getDefaultSnapshot());
            this._setEdit(this._model.getEdit());
            this._setRollback(this._model.getRollback());
            this._setView(this._model.getView());
            this._setCompare(this._model.getCompare());
            this._setRestore(this._model.getRestore());
            this._setDelete(this._model.getDelete());
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onDefaultSnapshot(newVal) {
        this._setDefaultSnapshot(newVal);
    }

    _onEdit(newVal) {
        this._setEdit(newVal);
    }

    _onRollback(newVal) {
        this._setRollback(newVal);
    }

    _onView(newVal) {
        this._setView(newVal);
    }

    _onCompare(newVal) {
        this._setCompare(newVal);
    }

    _onDelete(newVal) {
        this._setDelete(newVal);
    }

    _onDefaultSnapshotIconUserClick() {
        this._model._triggerUserCellAction({
            action: "defaultSnapshot",
        });
    }

    _onEditIconUserClick() {
        this._model._triggerUserCellAction({
            action: "edit",
        });
    }

    _onRollbackIconUserClick() {
        this._model._triggerUserCellAction({
            action: "rollback",
        });
    }

    _onViewIconUserClick() {
        this._model._triggerUserCellAction({
            action: "view",
        });
    }

    _onCompareIconUserClick() {
        this._model._triggerUserCellAction({
            action: "compare",
        });
    }

    _onRestoreIconUserClick() {
        this._model._triggerUserCellAction({
            action: "restore",
        });
    }

    _onDeleteIconUserClick() {
        this._model._triggerUserCellAction({
            action: "delete",
        });
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _setDefaultSnapshot(newVal) {
        if (newVal === true) {
            this._defaultSnapshotIcon.setDisabled(false);
        } else {
            this._defaultSnapshotIcon.setDisabled(true);
        }
    }

    _setEdit(newVal) {
        if (newVal === true) {
            this._editIcon.setDisabled(false);
        } else {
            this._editIcon.setDisabled(true);
        }
    }

    _setRollback(newVal) {
        if (newVal === true) {
            this._rollbackIcon.classList.remove("hidden");
        } else {
            this._rollbackIcon.classList.add("hidden");
        }
    }

    _setView(newVal) {
        if (newVal === true) {
            this._viewIcon.setDisabled(false);
        } else {
            this._viewIcon.setDisabled(true);
        }
    }

    _setDelete(newVal) {
        if (newVal === true) {
            this._deleteIcon.setDisabled(false);
        } else {
            this._deleteIcon.setDisabled(true);
        }
    }

    _setCompare(newVal) {
        if (newVal === true) {
            this._compareIcon.setDisabled(false);
        } else {
            this._compareIcon.setDisabled(true);
        }
    }

    _setRestore(newVal) {
        if (newVal === true) {
            this._restoreIcon.setDisabled(false);
        } else {
            this._restoreIcon.setDisabled(true);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = DeviceActionsColumnRenderer;
